import { AlertCircle, Clock, Users } from 'lucide-react';
import { NotificationRecipientTable } from './NotificationRecipientTable';

interface DeliverySummaryTabProps {
  broadcastUuid: string;
  /** "Class 5 - A · Parents" — what the first tile says under its count. */
  audience: string;
  totalRecipients: number;
  deliveredCount: number;
  failedCount: number;
  pendingCount: number;
}

export function NotificationDeliverySummaryTab({
  broadcastUuid,
  audience,
  totalRecipients,
  deliveredCount,
  failedCount,
  pendingCount,
}: DeliverySummaryTabProps) {
  const deliveryPct = totalRecipients > 0 ? Math.round((deliveredCount / totalRecipients) * 100) : 0;
  const failedPct = totalRecipients > 0 ? Math.round((failedCount / totalRecipients) * 100) : 0;
  const pendingPct = totalRecipients > 0 ? Math.round((pendingCount / totalRecipients) * 100) : 0;

  return (
    <div className="flex flex-col gap-6">
      {/* 4 Metrics Row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {/* Metric 1: Total recipients */}
        <div className="rounded-3xl border border-[#dcf3e4] dark:border-[#173e2a] bg-[#f2faf5] dark:bg-[#0d2117] p-5 flex items-center gap-4 shadow-lg shadow-emerald-500/10 hover:shadow-2xl hover:shadow-emerald-500/20 hover:-translate-y-1 transition-all duration-200">
          <div className="h-11 w-11 rounded-2xl bg-gradient-to-br from-[#10b981] to-[#34d399] text-white flex items-center justify-center shadow-md shadow-emerald-500/35 shrink-0">
            <Users className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <span className="text-[14px] font-extrabold text-emerald-900 dark:text-emerald-200 block leading-tight">
              Total recipients
            </span>
            <span className="font-black text-slate-900 dark:text-white text-2xl mt-0.5 block tracking-tight truncate">
              {totalRecipients}
            </span>
            {/* Was "All parents" regardless of who it went to. Says who now. */}
            <span
              className="text-xs font-bold text-slate-500 dark:text-slate-400 block mt-0.5 truncate"
              title={audience}
            >
              {audience}
            </span>
          </div>
        </div>

        {/* Metric 2: Delivered */}
        <div className="rounded-3xl border border-[#ebe8fd] dark:border-[#222144] bg-[#fbfaff] dark:bg-[#13162b] p-5 flex items-center gap-4 shadow-lg shadow-indigo-500/10 hover:shadow-2xl hover:shadow-indigo-500/20 hover:-translate-y-1 transition-all duration-200">
          <div className="h-12 w-12 rounded-[18px] bg-[#6355ff] text-white flex items-center justify-center shadow-md shadow-[#6355ff]/35 shrink-0">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="h-5 w-5 text-white"
            >
              <path d="M22 2L11 13" />
              <path d="M22 2L15 22L11 13L2 9L22 2Z" />
            </svg>
          </div>
          <div className="min-w-0 flex-1">
            <span className="text-[14px] font-extrabold text-indigo-900 dark:text-indigo-200 block leading-tight">
              Delivered
            </span>
            <span className="font-black text-slate-900 dark:text-white text-2xl mt-0.5 block tracking-tight truncate">
              {deliveredCount}
            </span>
            <span className="text-xs font-bold text-emerald-600 dark:text-emerald-400 block mt-0.5">
              {deliveryPct}% success
            </span>
          </div>
        </div>

        {/* Metric 3: Failed */}
        <div className="rounded-3xl border border-[#ffdce6] dark:border-[#47152c] bg-[#fff2f6] dark:bg-[#280e1b] p-5 flex items-center gap-4 shadow-lg shadow-rose-500/10 hover:shadow-2xl hover:shadow-rose-500/20 hover:-translate-y-1 transition-all duration-200">
          <div className="h-11 w-11 rounded-2xl bg-gradient-to-br from-[#f43f5e] to-[#fb7185] text-white flex items-center justify-center shadow-md shadow-rose-500/35 shrink-0">
            <AlertCircle className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <span className="text-[14px] font-extrabold text-rose-900 dark:text-rose-200 block leading-tight">
              Failed
            </span>
            <span className="font-black text-slate-900 dark:text-white text-2xl mt-0.5 block tracking-tight truncate">
              {failedCount}
            </span>
            <span className="text-xs font-bold text-slate-500 dark:text-slate-400 block mt-0.5">
              {failedPct}% failed
            </span>
          </div>
        </div>

        {/* Metric 4: Pending */}
        <div className="rounded-3xl border border-sky-200/90 dark:border-sky-950 bg-sky-50/60 dark:bg-[#0c1f2e] p-5 flex items-center gap-4 shadow-lg shadow-sky-500/10 hover:shadow-2xl hover:shadow-sky-500/20 hover:-translate-y-1 transition-all duration-200">
          <div className="h-11 w-11 rounded-2xl bg-gradient-to-br from-[#0ea5e9] to-[#38bdf8] text-white flex items-center justify-center shadow-md shadow-sky-500/35 shrink-0">
            <Clock className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <span className="text-[14px] font-extrabold text-sky-900 dark:text-sky-200 block leading-tight">
              Pending
            </span>
            <span className="font-black text-slate-900 dark:text-white text-2xl mt-0.5 block tracking-tight truncate">
              {pendingCount}
            </span>
            <span className="text-xs font-bold text-slate-500 dark:text-slate-400 block mt-0.5">
              {pendingPct}% queue
            </span>
          </div>
        </div>
      </div>

      {/* Recipient list section */}
      <NotificationRecipientTable broadcastUuid={broadcastUuid} />
    </div>
  );
}
